import { z } from "zod";
import { BanCategory, CastleRequestToken, Timestamp, UserId } from "../../common";
import { CheckoutOpener, ReturnPath } from "../../payments/v1alpha1/schemas";

const AppAttestProof = z
  .strictObject({
    keyId: z.string().min(1).max(256).meta({
      description: "The App Attest key's identifier, base64, as `generateKeyAsync` returned it.",
      example: "q3J3kX8xQf2b1k0cZ9X1w4cM2pYk3nqWb3eQ1x8sVfA=",
    }),
    attestation: z.string().min(1).max(32_768).meta({
      description: "Apple's attestation of the key, base64, from `attestKeyAsync`.",
      example: "o2NmbXRvYXBwbGUtYXBwYXR0ZXN0Z2F0dFN0bXSi...",
    }),
    challenge: z.string().min(1).max(512).meta({
      description: "The challenge from the App Attest challenge route, attested as-is.",
      example: "1791306638252.kq3J3kX8xQf2b1k0cZ9X1w.3m0V...",
    }),
  })
  .meta({
    id: "AppAttestProof",
    description: "An App Attest key, Apple's attestation of it, and the challenge it attested.",
  });

export const CreateSignInCheckBody = z
  .strictObject({
    castleToken: CastleRequestToken,
    recaptchaToken: z.string().min(1).max(8_192).optional().meta({
      description:
        "On the web, a token from `grecaptcha.enterprise.execute` with the score-only web key, for the `sign_in_check` action. Its score is recorded as a fact.",
      example: "03AFcWeA6b...",
    }),
    appAttest: AppAttestProof.optional().meta({
      description:
        "On iOS devices that support App Attest, a fresh key attested against a challenge from the App Attest challenge route. Whether it verifies is recorded as a fact.",
    }),
  })
  .meta({
    id: "CreateSignInCheckBody",
    description:
      "A Castle request token minted for this check, with the platform's invisible bot check when the client could run one.",
    example: {
      castleToken: "test|device:chrome_on_mac|ip:us-ca|policy.action:allow",
      recaptchaToken: "03AFcWeA6b...",
    },
  });
export type CreateSignInCheckBody = z.infer<typeof CreateSignInCheckBody>;

export const AppAttestChallengeResponse = z
  .object({
    challenge: z.string().meta({
      description: "Pass it to `attestKey` as the challenge, then post it with the attestation.",
      example: "1791306638252.kq3J3kX8xQf2b1k0cZ9X1w.3m0V...",
    }),
    expiresAt: Timestamp.meta({ description: "When the challenge stops being accepted." }),
  })
  .meta({
    id: "AppAttestChallengeResponse",
    description: "A challenge for one App Attest attestation by this session.",
  });
export type AppAttestChallengeResponse = z.infer<typeof AppAttestChallengeResponse>;

export const CreateCardVerificationBody = z
  .strictObject({
    returnPath: ReturnPath.optional().meta({
      description:
        "The page on this site Stripe returns the web app to, with `card_verification_id` added.",
      example: "/play/3f1c2e9a",
    }),
    openedFrom: CheckoutOpener.optional().meta({
      description:
        "`ios_app` opens Stripe in Safari, laid out for a phone, and returns to the app instead of `returnPath`.",
    }),
  })
  .meta({
    id: "CreateCardVerificationBody",
    description: "Where Stripe sends the person back to once the card is added.",
  });
export type CreateCardVerificationBody = z.infer<typeof CreateCardVerificationBody>;

export const CardVerificationResponse = z
  .object({
    cardVerificationId: z.string().meta({
      description: "The Stripe Checkout Session that adds the card.",
      example: "cs_test_a1b2c3",
    }),
    url: z.url().meta({ description: "Send the person here to add the card." }),
  })
  .meta({
    id: "CardVerificationResponse",
    description: "A card verification to send the person to.",
  });
export type CardVerificationResponse = z.infer<typeof CardVerificationResponse>;

export const CardVerificationId = z
  .string()
  .max(255)
  .regex(/^cs_(test|live)_[A-Za-z0-9]+$/, "A Stripe Checkout Session id.");

/** The query parameter the web app finds the card verification in when Stripe sends it back. */
export const CARD_VERIFICATION_RETURN_PARAM = "card_verification_id";

/**
 * `verified`: the card proves a person, which lowers the account's standing score. `not_finished`:
 * the person left Stripe before the card was saved, or Stripe is still confirming it.
 * `card_overused`: the card already verifies as many accounts as a card can. `card_prepaid`:
 * prepaid cards don't prove a person.
 */
export const CardVerificationOutcome = z
  .object({ status: z.enum(["verified", "not_finished", "card_overused", "card_prepaid"]) })
  .meta({ id: "CardVerificationOutcome", description: "What the added card proved." });
export type CardVerificationOutcome = z.infer<typeof CardVerificationOutcome>;

/** The reCAPTCHA action a sign-in check's token is issued for. */
export const SIGN_IN_CHECK_RECAPTCHA_ACTION = "sign_in_check";

export const AccountRiskParams = z.object({ userId: UserId }).meta({
  id: "AccountRiskParams",
  description: "The account to read, ban, or unban.",
});
export type AccountRiskParams = z.infer<typeof AccountRiskParams>;

export const RiskFactKind = z
  .enum([
    "castle_login",
    "castle_registration",
    "recaptcha",
    "app_attest",
    "card",
    "phone",
    "identity",
  ])
  .meta({
    id: "RiskFactKind",
    description:
      "What a fact is about: Castle's sign-in or registration verdict, a reCAPTCHA score, an App Attest attestation, or a verified card, phone, or ID document.",
  });
export type RiskFactKind = z.infer<typeof RiskFactKind>;

export const RiskFactRecord = z
  .object({
    kind: RiskFactKind,
    outcome: z.string().meta({
      description:
        "`allow`, `challenge`, or `deny` for Castle; `scored` for reCAPTCHA; `passed` or `failed` for App Attest; `verified` for a card, phone, or ID.",
      example: "allow",
    }),
    score: z.number().nullable().meta({
      description: "reCAPTCHA's score, from 0 (likely a bot) to 1 (likely a person).",
      example: 0.9,
    }),
    recaptchaReasons: z
      .array(z.string())
      .nullable()
      .meta({ description: "Why reCAPTCHA scored as it did.", example: ["AUTOMATION"] }),
    castleResponse: z.record(z.string(), z.unknown()).nullable().meta({
      description:
        "Castle's whole Risk API answer for a Castle verdict: its scores, signals, policy, device, IP and location, and metrics.",
    }),
    sessionId: z.uuid().nullable().meta({ description: "The session it happened in." }),
    firstObservedAt: Timestamp,
    observedAt: Timestamp.meta({ description: "The latest time it happened." }),
  })
  .meta({ id: "RiskFactRecord", description: "One fact about an account." });
export type RiskFactRecord = z.infer<typeof RiskFactRecord>;

export const AccountTier = z.enum(["trusted", "normal", "suspicious", "restricted"]).meta({
  id: "AccountTier",
  description:
    "The band the standing score falls in: trusted below 20, normal below 45, suspicious below 70, restricted from 70.",
});
export type AccountTier = z.infer<typeof AccountTier>;

export const StandingSignal = z
  .object({
    kind: z.union([RiskFactKind, z.literal("shared_device")]).meta({
      description:
        "The fact's kind, or `shared_device` when Castle scored five or more accounts on one of the account's devices in the last 30 days.",
    }),
    outcome: z.string().meta({
      description:
        "The fact's outcome as the version judged it, such as `person` or `bot` for a reCAPTCHA score.",
    }),
    points: z.number().meta({ description: "What the fact adds now, after decay.", example: -8.5 }),
    fullPoints: z.number().meta({ description: "What it added when it happened.", example: -10 }),
    decay: z.enum(["step", "linear", "exponential"]),
    observedAt: Timestamp,
  })
  .meta({ id: "StandingSignal", description: "One fact's part in a standing score." });

export const AccountStanding = z
  .object({
    version: z.number().int().meta({
      description: "The version of `risk.account_standing` that scored it.",
      example: 1,
    }),
    score: z.number().int().min(0).max(100).meta({
      description: "Suspicion from 0 to 100; higher is worse.",
      example: 35,
    }),
    tier: AccountTier,
    breakdown: z
      .object({
        baseline: z.number().meta({ description: "The score with no facts.", example: 50 }),
        signals: z
          .array(StandingSignal)
          .meta({ description: "The newest live fact of each kind, and a shared device." }),
        floors: z
          .array(z.object({ rule: z.string(), score: z.number() }))
          .meta({ description: "The floor rules that raised the score, and to what." }),
      })
      .meta({ description: "What the score was made of." }),
  })
  .meta({ id: "AccountStanding", description: "How the risk policy sees an account right now." });
export type AccountStanding = z.infer<typeof AccountStanding>;

export const AccountBan = z
  .object({
    category: BanCategory,
    note: z.string().nullable().meta({
      description: "What the staff member or moderation wrote down.",
      example: "Resold free credits through the AI routes.",
    }),
  })
  .meta({ id: "AccountBan", description: "Why an account is banned." });
export type AccountBan = z.infer<typeof AccountBan>;

export const AccountRiskResponse = z
  .object({
    userId: UserId,
    ban: AccountBan.nullable().meta({ description: "The account's ban, or null." }),
    standing: AccountStanding,
    facts: z.array(RiskFactRecord).meta({ description: "Newest first." }),
  })
  .meta({ id: "AccountRiskResponse", description: "An account's ban, standing, and risk facts." });
export type AccountRiskResponse = z.infer<typeof AccountRiskResponse>;

export const BanAccountBody = z
  .strictObject({
    note: z.string().trim().min(1).max(500).meta({
      description: "Why staff banned the account, for whoever reads it next.",
      example: "Resold free credits through the AI routes.",
    }),
  })
  .meta({ id: "BanAccountBody", description: "The staff note a ban carries." });
export type BanAccountBody = z.infer<typeof BanAccountBody>;
